import Select from '@xver/components/Atoms/Select/Select';
import { useMemo } from 'react';
import addressJson from '@xver/assets/json/address.json';
import { Address, AddressObject, Amphure, Tambon } from '@xver/@types/address';
import { InputField } from '@xver/components/Atoms';

export interface ThaiAddressValue {
    province: string;
    district: string;
    subDistrict: string;
    postalCode: string;
}

export interface ThaiAddressErrors {
    province?: string;
    district?: string;
    subDistrict?: string;
    postalCode?: string;
}

export interface ThaiAddressLabels {
    province?: string;
    provincePlaceholder?: string;
    district?: string;
    districtPlaceholder?: string;
    subDistrict?: string;
    subDistrictPlaceholder?: string;
    postalCode?: string;
    postalCodePlaceholder?: string;
}

interface ThaiAddressSelectorProps {
    value: ThaiAddressValue;
    onChange: (next: ThaiAddressValue) => void;
    errors?: ThaiAddressErrors;
    labels?: ThaiAddressLabels;
    disabled?: boolean;
}

const SELECT_STYLES = {
    input: {
        backgroundColor:
            'var(--color-input-bg, color-mix(in srgb, var(--color-background) 97%, var(--color-button-bg)))',
        borderColor: 'var(--color-input-border, var(--color-border))',
        color: 'var(--color-text, currentColor)',
        fontFamily: 'var(--font-sans)',
    },
    label: {
        fontSize: '0.875rem',
        fontWeight: 500,
        marginBottom: '0.25rem',
    },
    error: {
        fontSize: '0.75rem',
        marginTop: '0.25rem',
    },
};

const addressData = addressJson as unknown as Address;

export default function ThaiAddressSelector({
    value,
    onChange,
    errors,
    labels,
    disabled,
}: ThaiAddressSelectorProps) {
    const provinceOptions = useMemo(
        () =>
            addressData.map((p) => ({
                value: p.name_th,
                label: p.name_th,
            })),
        []
    );

    const provinceObj = useMemo<AddressObject | undefined>(
        () => addressData.find((p) => p.name_th === value.province),
        [value.province]
    );

    const districtOptions = useMemo(
        () =>
            provinceObj?.amphure.map((d) => ({
                value: d.name_th,
                label: d.name_th,
            })) ?? [],
        [provinceObj]
    );

    const districtObj = useMemo<Amphure | undefined>(
        () => provinceObj?.amphure.find((d) => d.name_th === value.district),
        [provinceObj, value.district]
    );

    const subDistrictOptions = useMemo(
        () =>
            districtObj?.tambon.map((t) => ({
                value: t.name_th,
                label: t.name_th,
            })) ?? [],
        [districtObj]
    );

    const handleProvinceChange = (next: string | null) => {
        onChange({
            province: next ?? '',
            district: '',
            subDistrict: '',
            postalCode: '',
        });
    };

    const handleDistrictChange = (next: string | null) => {
        onChange({
            ...value,
            district: next ?? '',
            subDistrict: '',
            postalCode: '',
        });
    };

    const handleSubDistrictChange = (next: string | null) => {
        const subDistrict = next ?? '';
        let postalCode = '';
        if (subDistrict && districtObj) {
            const tambon = districtObj.tambon.find((t) => t.name_th === subDistrict);
            if (tambon) {
                postalCode = String(tambon.zip_code);
            }
        }
        onChange({ ...value, subDistrict, postalCode });
    };

    return (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
                label={labels?.province ?? 'จังหวัด'}
                placeholder={labels?.provincePlaceholder ?? 'เลือกจังหวัด'}
                data={provinceOptions}
                value={value.province || null}
                onChange={handleProvinceChange}
                disabled={disabled}
                searchable
                error={errors?.province}
                styles={SELECT_STYLES}
            />
            <Select
                label={labels?.district ?? 'อำเภอ/เขต'}
                placeholder={labels?.districtPlaceholder ?? 'เลือกอำเภอ/เขต'}
                data={districtOptions}
                value={value.district || null}
                onChange={handleDistrictChange}
                disabled={disabled || !value.province}
                searchable
                error={errors?.district}
                styles={SELECT_STYLES}
            />
            <Select
                label={labels?.subDistrict ?? 'ตำบล/แขวง'}
                placeholder={labels?.subDistrictPlaceholder ?? 'เลือกตำบล/แขวง'}
                data={subDistrictOptions}
                value={value.subDistrict || null}
                onChange={handleSubDistrictChange}
                disabled={disabled || !value.district}
                searchable
                error={errors?.subDistrict}
                styles={SELECT_STYLES}
            />
            <InputField
                size="md"
                label={labels?.postalCode ?? 'รหัสไปรษณีย์'}
                placeholder={labels?.postalCodePlaceholder ?? '00000'}
                value={value.postalCode}
                onChange={(e) =>
                    onChange({
                        ...value,
                        postalCode: e.target.value.replace(/\D/g, '').slice(0, 5),
                    })
                }
                disabled={disabled}
                error={errors?.postalCode}
                maxLength={5}
            />
        </div>
    );
}

export function concatThaiAddress(streetLine: string, value: ThaiAddressValue): string {
    const parts: string[] = [];
    const trimmed = streetLine.trim();
    if (trimmed) parts.push(trimmed);
    if (value.subDistrict) parts.push(`ต.${value.subDistrict}`);
    if (value.district) parts.push(`อ.${value.district}`);
    if (value.province) parts.push(`จ.${value.province}`);
    if (value.postalCode) parts.push(value.postalCode);
    return parts.join(' ');
}

export function parseTambonFromAddress(
    province: string,
    streetLine?: string
): Pick<ThaiAddressValue, 'province' | 'district' | 'subDistrict' | 'postalCode'> {
    void streetLine;
    return {
        province,
        district: '',
        subDistrict: '',
        postalCode: '',
    };
}
